/**
 * Multi-Persona Improvement Loop Hook
 *
 * Orchestrates the episode improvement cycle with multiple eval personas:
 * For each iteration: parallel (Simulate + ContentJudge per persona) → Summarize → Improve → Repeat
 *
 * Story: 24.4 - Multi-Persona Content Improvement Engine
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  EpisodeTestingData,
  SimulationResult,
  ContentJudgeResult,
  JudgeSummaryResult,
  EpisodeImprovementProposal,
  IterationRecord,
} from '../services/testingApi';
import type { EvalPersona } from '../services/personaApi';

// ============================================================================
// Types
// ============================================================================

export interface EpisodeFields {
  context: string;
  scenario: string;
  goals: string[];
  personas: Array<{ name: string; role?: string; description: string }>;
  complexityLevel: number;
}

export interface PersonaResult {
  personaName: string;
  personaCefr: string | null;
  personaDescription: string;
  simulationResult: SimulationResult;
  judgeResult: ContentJudgeResult;
}

export interface MultiPersonaIterationState {
  iteration: number;
  episodeData: EpisodeFields;
  perPersonaResults: PersonaResult[];
  summary: JudgeSummaryResult | null;
  proposal: EpisodeImprovementProposal | null;
  changesApplied: string;
}

export type LoopStatus =
  | 'idle'
  | 'configuring'
  | 'simulating'
  | 'judging'
  | 'summarizing'
  | 'improving'
  | 'complete';

export type StopReason = 'threshold_met' | 'max_iterations' | 'cancelled' | 'error' | null;

export interface MultiPersonaLoopConfig {
  maxIterations: number;
  selectedPersonaIds: number[];
}

export interface MultiPersonaLoopState {
  baseline: MultiPersonaIterationState | null;
  iterations: MultiPersonaIterationState[];
  status: LoopStatus;
  config: MultiPersonaLoopConfig;
  stopReason: StopReason;
  stopMessage: string | null;
  currentIteration: number;
  simulatingPersonaIndex: number;
}

// API function signatures
interface RunSimulationFn {
  (params: {
    episodeId: string;
    language: string;
    personaInstructions: string;
    maxTurns: number;
    contextOverride?: string;
    scenarioOverride?: string;
    goalsOverride?: string[];
    personasOverride?: Array<Record<string, unknown>>;
    complexityLevelOverride?: number;
  }): Promise<SimulationResult>;
}

interface ContentJudgeFn {
  (params: {
    language: string;
    episodeContext: string;
    episodeScenario: string;
    episodeGoals: string[];
    episodePersonas: Array<Record<string, unknown>>;
    complexityLevel: number;
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
    toolCalls: Array<{
      toolName: string;
      arguments?: Record<string, unknown> | null;
      afterTurn: number;
    }>;
    evalPersonaName: string;
    evalPersonaDescription: string;
  }): Promise<ContentJudgeResult>;
}

interface SummarizeJudgesFn {
  (params: {
    language: string;
    episodeContext: string;
    episodeScenario: string;
    episodeGoals: string[];
    episodePersonas: Array<Record<string, unknown>>;
    complexityLevel: number;
    judgeResults: Array<{
      evalPersonaName: string;
      evalPersonaCefr: string | null;
      judgeResult: ContentJudgeResult;
    }>;
    iterationHistory: Array<{
      iteration: number;
      changesApplied: string;
      scoresBefore: Record<string, number>;
      scoresAfter: Record<string, number>;
    }>;
  }): Promise<JudgeSummaryResult>;
}

interface ImproveEpisodeFn {
  (params: {
    episodeContext: string;
    episodeScenario: string;
    episodeGoals: string[];
    episodePersonas: Array<Record<string, unknown>>;
    complexityLevel: number;
    languageCode: string;
    judgeSummary: JudgeSummaryResult;
    iterationHistory: IterationRecord[];
  }): Promise<EpisodeImprovementProposal>;
}

// ============================================================================
// Hook
// ============================================================================

const DEFAULT_CONFIG: MultiPersonaLoopConfig = {
  maxIterations: 5,
  selectedPersonaIds: [],
};

function extractEpisodeFields(episode: EpisodeTestingData): EpisodeFields {
  return {
    context: episode.context,
    scenario: episode.scenario,
    goals: [...episode.goals],
    personas: episode.personas.map((p) => ({
      name: p.name,
      role: (p as Record<string, unknown>).role as string | undefined,
      description: p.description,
    })),
    complexityLevel: episode.complexityLevel,
  };
}

function applyChanges(current: EpisodeFields, changes: Record<string, unknown>): EpisodeFields {
  // Merge role from current personas into changed personas (LLM may not return role)
  let personas = current.personas;
  if (changes.personas) {
    const changedPersonas = changes.personas as Array<{
      name: string;
      role?: string;
      description: string;
    }>;
    personas = changedPersonas.map((p) => {
      const existing = current.personas.find((cp) => cp.name === p.name);
      return { ...p, role: p.role ?? existing?.role };
    });
  }

  return {
    context: (changes.context as string) ?? current.context,
    scenario: (changes.scenario as string) ?? current.scenario,
    goals: (changes.goals as string[]) ?? current.goals,
    personas,
    complexityLevel: current.complexityLevel, // Never changed by improver
  };
}

function buildIterationHistory(iterations: MultiPersonaIterationState[]): IterationRecord[] {
  return iterations.map((it) => ({
    iteration: it.iteration,
    changesMade: it.proposal?.proposedChanges ?? {},
    rationale: it.changesApplied || it.proposal?.rationale || '',
    scores: Object.fromEntries(
      it.perPersonaResults.map((r) => [r.personaName, r.judgeResult.overallScore])
    ),
  }));
}

function buildIterationSummaries(
  iterations: MultiPersonaIterationState[],
  baseline: MultiPersonaIterationState | null
) {
  const summaries: Array<{
    iteration: number;
    changesApplied: string;
    scoresBefore: Record<string, number>;
    scoresAfter: Record<string, number>;
  }> = [];

  for (let i = 0; i < iterations.length; i++) {
    const it = iterations[i];
    const prev = i === 0 ? baseline : iterations[i - 1];
    summaries.push({
      iteration: it.iteration,
      changesApplied: it.changesApplied,
      scoresBefore: Object.fromEntries(
        (prev?.perPersonaResults || []).map((r) => [r.personaName, r.judgeResult.overallScore])
      ),
      scoresAfter: Object.fromEntries(
        it.perPersonaResults.map((r) => [r.personaName, r.judgeResult.overallScore])
      ),
    });
  }
  return summaries;
}

export function useImprovementLoop(
  episode: EpisodeTestingData | null,
  evalPersonas: EvalPersona[] | undefined,
  runSimulation: RunSimulationFn,
  contentJudge: ContentJudgeFn,
  summarizeJudges: SummarizeJudgesFn,
  improveEpisode: ImproveEpisodeFn
) {
  const [state, setState] = useState<MultiPersonaLoopState>({
    baseline: null,
    iterations: [],
    status: 'idle',
    config: DEFAULT_CONFIG,
    stopReason: null,
    stopMessage: null,
    currentIteration: 0,
    simulatingPersonaIndex: 0,
  });

  const cancelledRef = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const setConfig = useCallback((config: Partial<MultiPersonaLoopConfig>) => {
    setState((prev) => ({ ...prev, config: { ...prev.config, ...config } }));
  }, []);

  const openConfig = useCallback(() => {
    // Default: select all system personas
    const systemIds = evalPersonas?.filter((p) => p.isSystem).map((p) => p.id) || [];
    setState((prev) => ({
      ...prev,
      status: 'configuring',
      config: { ...prev.config, selectedPersonaIds: systemIds },
    }));
  }, [evalPersonas]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setState((prev) => ({
      ...prev,
      status: 'complete',
      stopReason: 'cancelled',
      stopMessage: 'Cancelled by user',
    }));
  }, []);

  const discardAll = useCallback(() => {
    cancelledRef.current = false;
    setState({
      baseline: null,
      iterations: [],
      status: 'idle',
      config: DEFAULT_CONFIG,
      stopReason: null,
      stopMessage: null,
      currentIteration: 0,
      simulatingPersonaIndex: 0,
    });
  }, []);

  // Run simulation + judge for a single persona
  const runPersonaPipeline = async (
    persona: EvalPersona,
    episodeData: EpisodeFields,
    language: string,
    episodeId: string
  ): Promise<PersonaResult> => {
    // 1. Simulate
    const simResult = await runSimulation({
      episodeId,
      language,
      personaInstructions: persona.description,
      maxTurns: 50,
      contextOverride: episodeData.context,
      scenarioOverride: episodeData.scenario,
      goalsOverride: episodeData.goals,
      personasOverride: episodeData.personas as Array<Record<string, unknown>>,
      complexityLevelOverride: episodeData.complexityLevel,
    });

    // 2. Content judge
    const judgeResult = await contentJudge({
      language,
      episodeContext: episodeData.context,
      episodeScenario: episodeData.scenario,
      episodeGoals: episodeData.goals,
      episodePersonas: episodeData.personas as Array<Record<string, unknown>>,
      complexityLevel: episodeData.complexityLevel,
      conversationHistory: simResult.conversationHistory,
      toolCalls: simResult.toolCalls,
      evalPersonaName: persona.name,
      evalPersonaDescription: persona.description,
    });

    return {
      personaName: persona.name,
      personaCefr: persona.cefrCode,
      personaDescription: persona.description,
      simulationResult: simResult,
      judgeResult,
    };
  };

  // Run a full iteration: simulate all personas, summarize, check stop, improve
  const runIteration = async (
    iterationNum: number,
    episodeData: EpisodeFields,
    selectedPersonas: EvalPersona[],
    previousIterations: MultiPersonaIterationState[],
    baselineState: MultiPersonaIterationState | null
  ): Promise<'continue' | 'stop'> => {
    if (!episode || cancelledRef.current) return 'stop';

    const language = episode.language;
    const episodeId = episode.episodeId;

    // 1. Simulate + judge all personas in parallel
    setState((prev) => ({ ...prev, status: 'simulating', currentIteration: iterationNum }));

    let perPersonaResults: PersonaResult[];
    let completedCount = 0;
    try {
      perPersonaResults = await Promise.all(
        selectedPersonas.map((persona) => {
          return runPersonaPipeline(persona, episodeData, language, episodeId).then((result) => {
            // Track progress as each persona completes
            completedCount++;
            setState((prev) => ({ ...prev, simulatingPersonaIndex: completedCount - 1 }));
            return result;
          });
        })
      );
    } catch (error) {
      setState((prev) => ({
        ...prev,
        status: 'complete',
        stopReason: 'error',
        stopMessage: `Simulation/judge failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
      return 'stop';
    }

    if (cancelledRef.current) return 'stop';

    // 2. Summarize
    setState((prev) => ({ ...prev, status: 'summarizing' }));

    let summary: JudgeSummaryResult;
    try {
      summary = await summarizeJudges({
        language,
        episodeContext: episodeData.context,
        episodeScenario: episodeData.scenario,
        episodeGoals: episodeData.goals,
        episodePersonas: episodeData.personas as Array<Record<string, unknown>>,
        complexityLevel: episodeData.complexityLevel,
        judgeResults: perPersonaResults.map((r) => ({
          evalPersonaName: r.personaName,
          evalPersonaCefr: r.personaCefr,
          judgeResult: r.judgeResult,
        })),
        iterationHistory: buildIterationSummaries(previousIterations, baselineState),
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        status: 'complete',
        stopReason: 'error',
        stopMessage: `Summarization failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
      return 'stop';
    }

    if (cancelledRef.current) return 'stop';

    // Build iteration state (without proposal yet)
    const iterState: MultiPersonaIterationState = {
      iteration: iterationNum,
      episodeData,
      perPersonaResults,
      summary,
      proposal: null,
      changesApplied: '',
    };

    // Update state with this iteration's results
    const updatedIterations = [...previousIterations, iterState];
    setState((prev) => ({
      ...prev,
      iterations: iterationNum === 0 ? [] : updatedIterations.filter((it) => it.iteration > 0),
      baseline: iterationNum === 0 ? iterState : prev.baseline,
    }));

    // 3. Check stop: all passed
    if (summary.allPassed) {
      setState((prev) => ({
        ...prev,
        status: 'complete',
        stopReason: 'threshold_met',
        stopMessage: 'All judges scored 8+',
      }));
      return 'stop';
    }

    // 4. Check stop: max iterations (baseline is iteration 0, don't count it)
    const currentConfig = stateRef.current.config;
    if (iterationNum > 0 && iterationNum >= currentConfig.maxIterations) {
      setState((prev) => ({
        ...prev,
        status: 'complete',
        stopReason: 'max_iterations',
        stopMessage: `Max iterations reached (${currentConfig.maxIterations})`,
      }));
      return 'stop';
    }

    // 5. Improve
    setState((prev) => ({ ...prev, status: 'improving' }));

    let proposal: EpisodeImprovementProposal;
    try {
      const allCompletedIterations = updatedIterations.filter((it) => it.iteration > 0);
      proposal = await improveEpisode({
        episodeContext: episodeData.context,
        episodeScenario: episodeData.scenario,
        episodeGoals: episodeData.goals,
        episodePersonas: episodeData.personas as Array<Record<string, unknown>>,
        complexityLevel: episodeData.complexityLevel,
        languageCode: language,
        judgeSummary: summary,
        iterationHistory: buildIterationHistory(allCompletedIterations),
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        status: 'complete',
        stopReason: 'error',
        stopMessage: `Improvement failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
      return 'stop';
    }

    if (cancelledRef.current) return 'stop';

    // 6. Apply changes
    const newEpisodeData = applyChanges(episodeData, proposal.proposedChanges);
    const changesApplied = proposal.rationale;

    // Update iteration with proposal
    iterState.proposal = proposal;
    iterState.changesApplied = changesApplied;

    const finalIterations = [...previousIterations, iterState];
    setState((prev) => ({
      ...prev,
      iterations: finalIterations.filter((it) => it.iteration > 0),
      baseline: iterationNum === 0 ? iterState : prev.baseline,
    }));

    // 7. Recurse with next iteration
    const nextIterNum = iterationNum + 1;
    return runIteration(
      nextIterNum,
      newEpisodeData,
      selectedPersonas,
      finalIterations,
      iterationNum === 0 ? iterState : baselineState
    );
  };

  // Start the loop
  const startLoop = useCallback(async () => {
    if (!episode || !evalPersonas) return;

    const currentConfig = stateRef.current.config;
    const selectedPersonas = evalPersonas.filter((p) =>
      currentConfig.selectedPersonaIds.includes(p.id)
    );
    if (selectedPersonas.length === 0) return;

    cancelledRef.current = false;
    const baselineEpisodeData = extractEpisodeFields(episode);

    setState((prev) => ({
      ...prev,
      baseline: null,
      iterations: [],
      status: 'simulating',
      stopReason: null,
      stopMessage: null,
      currentIteration: 0,
    }));

    // Run baseline (iteration 0) then continue
    await runIteration(0, baselineEpisodeData, selectedPersonas, [], null);
  }, [episode, evalPersonas, runSimulation, contentJudge, summarizeJudges, improveEpisode]);

  return {
    state,
    setConfig,
    openConfig,
    startLoop,
    cancel,
    discardAll,
  };
}
